import { Popper } from '@mui/material';
import { useSelectedText } from '@remirror/react';
import { memo, useCallback, useEffect, useMemo, useRef, type MouseEvent } from 'react';
import { Copy } from 'react-feather';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { FloatingWrapper } from '../Remirror/FloatingWrapper';
import { PopperMenu, PopperMenuItem } from '../Popper/PopperMenu';
import { PopperWrapper } from '../Popper/PopperWrapper';
import { useTextSelectionPositioner } from './hooks/useTextSelectionPositioner';

const ICON_SIZE = 22;

type Props = {
  disabled?: boolean;
};

export const TranscriptSelectionActionsMenu = memo(({ disabled = false }: Props) => {
  const position = useTextSelectionPositioner();
  const selectedText = useSelectedText();
  const copyToClipboard = useCopyToClipboard();

  const containerRef = useRef<HTMLDivElement>(null);
  const selectedTextRef = useRef<string>('');

  useEffect(() => {
    if (!selectedText) {
      selectedTextRef.current = '';
    }
    if (selectedText && selectedText.trim().length > 0) {
      selectedTextRef.current = selectedText;
    }
  }, [selectedText]);

  const open = useMemo(() => {
    if (disabled) return false;

    if (!selectedText) return false;

    return position.visible && selectedText.trim().length > 0;
  }, [disabled, position.visible, selectedText]);

  const handleCopyText = useCallback(() => {
    copyToClipboard(selectedTextRef.current);
  }, [copyToClipboard]);

  const handleCopyMouseDown = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
  }, []);

  return (
    <FloatingWrapper position={position} containerRef={containerRef}>
      <Popper
        container={containerRef.current}
        open={open}
        anchorEl={position.element}
        placement="top"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ]}
        style={{ zIndex: 2 }}
      >
        <PopperWrapper>
          <PopperMenu>
            <PopperMenuItem onClick={handleCopyText} onMouseDown={handleCopyMouseDown}>
              <Copy size={ICON_SIZE} />
              Copy Text
            </PopperMenuItem>
          </PopperMenu>
        </PopperWrapper>
      </Popper>
    </FloatingWrapper>
  );
});

TranscriptSelectionActionsMenu.displayName = 'Transcript.SelectionActionsMenu';
